import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  Beaker,
  BrainCircuit,
  Check,
  CircleAlert,
  CopyPlus,
  History,
  Power,
  Rocket,
  Save,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

type SkillStatus = "draft" | "published" | "disabled" | "archived";
type SkillScope = "editor3d" | "workspace";

interface EvaluationCase {
  prompt: string;
  shouldActivate: boolean;
}

interface SkillDraft {
  name: string;
  description: string;
  category: string;
  tags: string[];
  scopes: SkillScope[];
  automaticActivation: boolean;
  priority: number;
  requiredCapabilities: string[];
  instructionsMarkdown: string;
  activationExamples: string[];
  negativeExamples: string[];
  evaluationCases: EvaluationCase[];
}

interface Skill extends SkillDraft {
  id: string;
  slug: string;
  draft: SkillDraft;
  draftRevision: number;
  status: SkillStatus;
  currentVersion: number | null;
  currentVersionId: string | null;
  updatedAt: string;
  versions?: SkillVersion[];
}

interface SkillVersion extends SkillDraft {
  id: string;
  version: number;
  checksum: string;
  publishedAt: string;
  publishedBy: string;
}

interface SkillValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  instructionCharacters?: number;
  estimatedInstructionTokens?: number;
}

const EMPTY_DRAFT: SkillDraft = {
  name: "",
  description: "",
  category: "Concepção",
  tags: [],
  scopes: ["editor3d"],
  automaticActivation: true,
  priority: 0,
  requiredCapabilities: [],
  instructionsMarkdown: "# Nova skill\n\nDescreva o método profissional que o meX deve aplicar quando esta skill estiver ativa.",
  activationExamples: [],
  negativeExamples: [],
  evaluationCases: [],
};

export function SkillsAdminSection() {
  const { session } = useAuth();
  const client = useQueryClient();
  const canWrite = Boolean(session?.platformRoles.some((role) => ["platform_admin", "ai_skill_admin"].includes(role)));
  const query = useQuery({ queryKey: ["internal-ai-skills"], queryFn: () => api<{ data: Skill[] }>("/internal/ai-skills") });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [slug, setSlug] = useState("");
  const [draft, setDraft] = useState<SkillDraft>(EMPTY_DRAFT);
  const [validation, setValidation] = useState<SkillValidation | null>(null);
  const [previewPrompt, setPreviewPrompt] = useState("");
  const [preview, setPreview] = useState<Array<{ skill: Skill; score: number }>>([]);

  const detail = useQuery({
    queryKey: ["internal-ai-skill", selectedId],
    queryFn: () => api<{ data: Skill }>(`/internal/ai-skills/${encodeURIComponent(selectedId!)}`),
    enabled: Boolean(selectedId),
  });
  const selected = detail.data?.data ?? query.data?.data.find((skill) => skill.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected || creating) return;
    setSlug(selected.slug);
    setDraft(selected.draft ?? toDraft(selected));
    setValidation(null);
  }, [creating, selected]);

  useEffect(() => {
    if (!selectedId && !creating && query.data?.data.length) setSelectedId(query.data.data[0]!.id);
  }, [creating, query.data?.data, selectedId]);

  const refresh = async (id?: string) => {
    await client.invalidateQueries({ queryKey: ["internal-ai-skills"] });
    if (id) await client.invalidateQueries({ queryKey: ["internal-ai-skill", id] });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (creating) {
        return api<{ data: Skill }>("/internal/ai-skills", { method: "POST", body: { slug, ...draft } });
      }
      if (!selectedId) throw new Error("Selecione uma skill.");
      return api<{ data: Skill }>(`/internal/ai-skills/${encodeURIComponent(selectedId)}/draft`, { method: "PATCH", body: draft });
    },
    onSuccess: async (response) => {
      setCreating(false);
      setSelectedId(response.data.id);
      setValidation(null);
      await refresh(response.data.id);
    },
  });

  const validate = useMutation({
    mutationFn: async () => {
      if (!selectedId || creating) throw new Error("Salve a skill antes de validar.");
      await api(`/internal/ai-skills/${encodeURIComponent(selectedId)}/draft`, { method: "PATCH", body: draft });
      const response = await api<{ data: SkillValidation }>(`/internal/ai-skills/${encodeURIComponent(selectedId)}/validate`, { method: "POST" });
      return response.data;
    },
    onSuccess: setValidation,
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (!selectedId || creating) throw new Error("Salve a skill antes de publicar.");
      await api(`/internal/ai-skills/${encodeURIComponent(selectedId)}/draft`, { method: "PATCH", body: draft });
      return api(`/internal/ai-skills/${encodeURIComponent(selectedId)}/publish`, { method: "POST" });
    },
    onSuccess: async () => {
      setValidation({ valid: true, errors: [], warnings: [] });
      await refresh(selectedId ?? undefined);
    },
  });

  const statusMutation = useMutation({
    mutationFn: (status: "disable" | "archive") => api(`/internal/ai-skills/${encodeURIComponent(selectedId!)}/${status}`, { method: "POST" }),
    onSuccess: async () => refresh(selectedId ?? undefined),
  });

  const rollback = useMutation({
    mutationFn: (version: number) => api(`/internal/ai-skills/${encodeURIComponent(selectedId!)}/rollback`, { method: "POST", body: { version } }),
    onSuccess: async () => refresh(selectedId ?? undefined),
  });

  const previewMutation = useMutation({
    mutationFn: () => api<{ data: { candidates: Array<{ skill: Skill; score: number }> } }>("/internal/ai-skills/preview-activation", { method: "POST", body: { prompt: previewPrompt } }),
    onSuccess: (response) => setPreview(response.data.candidates),
  });

  const error = save.error ?? validate.error ?? publish.error ?? statusMutation.error ?? rollback.error ?? previewMutation.error ?? query.error ?? detail.error;
  const filtered = useMemo(() => query.data?.data ?? [], [query.data?.data]);

  const startNew = () => {
    setCreating(true);
    setSelectedId(null);
    setSlug("");
    setDraft({ ...EMPTY_DRAFT, tags: [], scopes: ["editor3d"], requiredCapabilities: [], activationExamples: [], negativeExamples: [], evaluationCases: [] });
    setValidation(null);
  };

  return (
    <section className="skills-page skills-page--embedded">
      <header className="settings-heading platform-admin-section-heading">
        <span><BrainCircuit size={21} /></span>
        <div>
          <h2>Skills</h2>
          <p>Métodos profissionais versionados, carregados sob demanda no agente do editor 3D.</p>
        </div>
        {canWrite && <button className="button button--primary" onClick={startNew}><CopyPlus size={17} /> Nova skill</button>}
      </header>

      <section className="settings-card skill-preview">
        <span><Beaker size={18} /><strong>Prévia de relevância</strong><small>Compara um pedido com os metadados publicados; a decisão final continua sendo do modelo.</small></span>
        <input value={previewPrompt} onChange={(event) => setPreviewPrompt(event.target.value)} placeholder="Ex.: projete uma cozinha completa neste ambiente" />
        <button className="button button--secondary" disabled={previewPrompt.trim().length < 3 || previewMutation.isPending} onClick={() => previewMutation.mutate()}><Search size={16} /> Avaliar</button>
        {preview.length > 0 && <div className="skill-preview__results">{preview.map(({ skill, score }) => <span key={skill.id}><strong>{skill.name}</strong><small>relevância {score.toFixed(2)}</small></span>)}</div>}
      </section>

      <div className="skills-layout">
        <aside className="settings-card skill-list">
          <div className="skill-list__heading"><BrainCircuit size={18} /><strong>{filtered.length} skills</strong></div>
          {filtered.map((skill) => (
            <button key={skill.id} className={selectedId === skill.id && !creating ? "active" : ""} onClick={() => { setCreating(false); setSelectedId(skill.id); }}>
              <span><strong>{skill.name}</strong><small>{skill.category} · {skill.slug}</small></span>
              <SkillStatusBadge status={skill.status} version={skill.currentVersion} />
            </button>
          ))}
          {!filtered.length && !query.isLoading && <p className="muted">Nenhuma skill cadastrada.</p>}
        </aside>

        <main className="settings-card skill-editor">
          {(creating || selected) ? (
            <>
              <div className="skill-editor__title">
                <div><strong>{creating ? "Nova skill" : selected?.name}</strong><small>{creating ? "O slug não poderá ser alterado depois da criação." : `Rascunho ${selected?.draftRevision} · versão publicada ${selected?.currentVersion ?? "—"}`}</small></div>
                {!creating && selected && <SkillStatusBadge status={selected.status} version={selected.currentVersion} />}
              </div>

              <div className="skill-form-grid">
                <Field label="Nome"><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
                <Field label="Slug"><input value={slug} disabled={!creating} onChange={(event) => setSlug(slugify(event.target.value))} placeholder="projetar-cozinha" /></Field>
                <Field label="Categoria"><input value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })} /></Field>
                <Field label="Prioridade"><input type="number" min={-100} max={100} value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: Number(event.target.value) })} /></Field>
              </div>

              <Field label="Descrição de ativação" hint="Explique quando usar e qual resultado profissional esta skill melhora.">
                <textarea rows={3} value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
              </Field>

              <div className="skill-form-grid">
                <Field label="Tags" hint="Separadas por vírgula"><input value={draft.tags.join(", ")} onChange={(event) => setDraft({ ...draft, tags: csv(event.target.value) })} /></Field>
                <Field label="Escopos">
                  <span className="skill-checks">
                    {(["editor3d", "workspace"] as SkillScope[]).map((scope) => <label key={scope}><input type="checkbox" checked={draft.scopes.includes(scope)} onChange={() => setDraft({ ...draft, scopes: toggle(draft.scopes, scope) })} /> {scope}</label>)}
                  </span>
                </Field>
              </div>

              <label className="skill-auto"><input type="checkbox" checked={draft.automaticActivation} onChange={(event) => setDraft({ ...draft, automaticActivation: event.target.checked })} /><span><strong>Disponível para ativação automática</strong><small>O modelo verá o nome e a descrição e decidirá se deve carregar o conteúdo.</small></span></label>

              <Field label="Capacidades necessárias" hint="Uma por linha. Servem para validar compatibilidade; não concedem autorização.">
                <textarea rows={5} value={draft.requiredCapabilities.join("\n")} onChange={(event) => setDraft({ ...draft, requiredCapabilities: lines(event.target.value) })} />
              </Field>

              <Field label="SKILL.md" hint={`${draft.instructionsMarkdown.length.toLocaleString("pt-BR")} caracteres · aproximadamente ${Math.ceil(draft.instructionsMarkdown.length / 4).toLocaleString("pt-BR")} tokens`}>
                <textarea className="skill-markdown" rows={22} value={draft.instructionsMarkdown} onChange={(event) => setDraft({ ...draft, instructionsMarkdown: event.target.value })} />
              </Field>

              <div className="skill-form-grid">
                <Field label="Deve ativar" hint="Um exemplo por linha"><textarea rows={7} value={draft.activationExamples.join("\n")} onChange={(event) => setDraft({ ...draft, activationExamples: lines(event.target.value) })} /></Field>
                <Field label="Não deve ativar" hint="Pedidos próximos, mas pontuais"><textarea rows={7} value={draft.negativeExamples.join("\n")} onChange={(event) => setDraft({ ...draft, negativeExamples: lines(event.target.value) })} /></Field>
              </div>

              <Field label="Casos de avaliação" hint="Comece cada linha com + para ativar e - para não ativar.">
                <textarea rows={7} value={formatEvaluationCases(draft.evaluationCases)} onChange={(event) => setDraft({ ...draft, evaluationCases: parseEvaluationCases(event.target.value) })} />
              </Field>

              {validation && <ValidationPanel validation={validation} />}
              {error && <p className="form-error"><CircleAlert size={15} /> {describeError(error)}</p>}

              <div className="skill-actions">
                {canWrite && <button className="button button--primary" disabled={save.isPending || !slug || !draft.name || draft.description.length < 20} onClick={() => save.mutate()}><Save size={16} /> Salvar rascunho</button>}
                <button className="button button--secondary" disabled={creating || !selectedId || validate.isPending} onClick={() => validate.mutate()}><Check size={16} /> Validar</button>
                {canWrite && <button className="button button--secondary" disabled={creating || !selectedId || publish.isPending} onClick={() => publish.mutate()}><Rocket size={16} /> Publicar versão</button>}
                {canWrite && !creating && selected?.status === "published" && <button className="button button--secondary" disabled={statusMutation.isPending} onClick={() => statusMutation.mutate("disable")}><Power size={16} /> Desabilitar</button>}
                {canWrite && !creating && selected?.status !== "archived" && <button className="button button--danger-soft" disabled={statusMutation.isPending} onClick={() => statusMutation.mutate("archive")}><Archive size={16} /> Arquivar</button>}
              </div>

              {!creating && selected?.versions?.length ? (
                <section className="skill-versions">
                  <h3><History size={17} /> Histórico publicado</h3>
                  {selected.versions.map((version) => (
                    <div key={version.id}>
                      <span><strong>Versão {version.version}</strong><small>{new Date(version.publishedAt).toLocaleString("pt-BR")} · {version.checksum.slice(0, 12)}</small></span>
                      {selected.currentVersion === version.version ? <em>Em uso</em> : canWrite ? <button className="text-button" onClick={() => rollback.mutate(version.version)}>Restaurar</button> : null}
                    </div>
                  ))}
                </section>
              ) : null}
            </>
          ) : <p className="muted">Selecione uma skill ou crie uma nova.</p>}
        </main>
      </div>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="skill-field"><span><strong>{label}</strong>{hint && <small>{hint}</small>}</span>{children}</label>;
}

function SkillStatusBadge({ status, version }: { status: SkillStatus; version: number | null }) {
  const labels: Record<SkillStatus, string> = { draft: "Rascunho", published: `Publicada${version ? ` v${version}` : ""}`, disabled: "Desabilitada", archived: "Arquivada" };
  return <em className={`skill-status skill-status--${status}`}>{labels[status]}</em>;
}

function ValidationPanel({ validation }: { validation: SkillValidation }) {
  return <section className={`skill-validation ${validation.valid ? "is-valid" : "is-invalid"}`}>
    <strong>{validation.valid ? "Skill válida para publicação" : "A skill precisa de correções"}</strong>
    {validation.errors.map((message) => <p key={message}>{message}</p>)}
    {validation.warnings.map((message) => <p key={message}>Aviso: {message}</p>)}
    {validation.instructionCharacters !== undefined && <small>{validation.instructionCharacters.toLocaleString("pt-BR")} caracteres · ~{validation.estimatedInstructionTokens?.toLocaleString("pt-BR")} tokens</small>}
  </section>;
}

function toDraft(skill: Skill): SkillDraft {
  const { id: _id, slug: _slug, draft: _draft, draftRevision: _revision, status: _status, currentVersion: _version, currentVersionId: _versionId, updatedAt: _updatedAt, versions: _versions, ...draft } = skill;
  return draft;
}

function slugify(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
}

function csv(value: string): string[] {
  return [...new Set(value.split(",").map((item) => item.trim()).filter(Boolean))];
}

function lines(value: string): string[] {
  return [...new Set(value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean))];
}

function toggle<T>(items: T[], item: T): T[] {
  return items.includes(item) ? items.filter((current) => current !== item) : [...items, item];
}

function formatEvaluationCases(cases: EvaluationCase[]): string {
  return cases.map((item) => `${item.shouldActivate ? "+" : "-"} ${item.prompt}`).join("\n");
}

function parseEvaluationCases(value: string): EvaluationCase[] {
  return value.split(/\r?\n/).map((line) => line.trim()).filter((line) => /^[+-]\s+/.test(line)).map((line) => ({ prompt: line.slice(1).trim(), shouldActivate: line.startsWith("+") }));
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : "Não foi possível concluir.";
}
